import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, Flex, List, Stack, Text } from '@chakra-ui/react';
import { TRPCClientError } from '@trpc/client';
import dynamic from 'next/dynamic';
import { usePathname } from 'next/navigation';
import { IoIosGitNetwork } from 'react-icons/io';
import useNetworks, { DEFAULT_NETWORK_SORT, NetworkSort, NetworkSortColumn, useRefetch } from '@/components/AppBar/NetworkPopover/hooks/useNetworks';
import { ReportConfirmationProps, ReportOptionsDialog } from '@/components/AppBar/NetworkPopover/ReportOptionsDialog';
import { buildNetworkSwitchUrl } from '@/components/AppShell/networkRoutes';
import NetworkBulkActionBar from '@/components/pages/Home/NetworkBulkActionBar';
import NetworkPersonFilterDropdown, { describePersonFilter, NetworkPersonFilter, parseStoredPersonFilter } from '@/components/pages/Home/NetworkOwnerFilter';
import NetworkTypeFilterDropdown, { describeTypeFilter, NetworkTypeFilterOption, parseStoredTypeFilter } from '@/components/pages/Home/NetworkTypeFilter';
import { Switch } from '@/components/ui/switch';
import { toaster } from '@/components/ui/toaster';
import ConfirmationDialog from '@/components/util-component/Dialog/ConfirmationDialog';
import { useGlobalState } from '@/global-state/useGlobalState';
import useNetwork from '@/hooks/network/useNetwork';
import { useInvalidateNetwork } from '@/hooks/network/useNetwork';
import { useJsonLocalStorage } from '@/hooks/useJsonLocalStorage';
import { useAsyncRoutePush } from '@/hooks/utils/useAsyncRouterPush';
import { InferenceAlgorithm } from '@/services/bayesianApi/models';
import { NETWORK_NAME_UNIQUE_VIOLATION_ERROR } from '@/services/constraints/errorMessages';
import { MAX_BULK_NETWORK_ACTION_SIZE } from '@/services/db/actions/network/runForEachNetwork';
import { NetworkStatus } from '@/services/models/enums';
import downloadJsonFile from '@/utils/general/fileDownload/downloadJsonFile';
import { ReleaseValidationError } from '@/utils/network/release/releaseValidationError';
import { safeJsonParse } from '@/utils/storage';
import { mutationWithFeedback, showError } from '@/utils/toast';
import { trpc } from '@/utils/trpc';
import { ConfirmationProps } from './CompareOptionsDialog';
import { ExportConfirmationProps, ExportOptionsDialog, ExportType } from './ExportDialogs';
import useBulkNetworkActions, { BulkNetworkAction, BulkNetworkTarget } from './hooks/useBulkNetworkActions';
import useCreateNetwork from './hooks/useCreateNetwork';
import useNetworkDelete from './hooks/useDeleteNetwork';
import { useDuplicateNetwork } from './hooks/useDuplicateNetwork';
import useExportAllNetworkRelatedData from './hooks/useExportAllNetworkRelatedData';
import useExportReleaseNetwork from './hooks/useExportReleaseNetwork';
import useGenerateNetworkReport from './hooks/useGenerateNetworkReport';
import useImportNetwork from './hooks/useImportNetwork';
import useNetworkComparison from './hooks/useNetworkComparsion';
import ImportNetworkButton from './ImportNetworkButton';
import CreateInput from '../SelectPopover/CreateInput';
import { generateAlgorithmMenuItem, generateApplyBasePopulationMenuItem, generateCompareMenuItem, generateDeleteMenuItem, generateDuplicateMenuItem, generateExportMenuItem, generateReleaseMenuItem, generateRenameMenuItem, generateReportMenuItem, generateTransitionDefaultsMenuItem, generateVersionMenuItem, generateViewCommitsMenuItem, generateRetireMenuItem, generateUnretireMenuItem } from '../SelectPopover/menuItems';
import ReleaseDialog from '../SelectPopover/ReleaseDialog';
import TransitionDefaultsDialog from '../SelectPopover/TransitionDefaultsDialog';
import VirtualizedSelectPopover, { MenuItems, Option, OptionSelection, Title } from '../SelectPopover/VirtualizedSelectPopover';

// The legacy `networkBrowserOwnerFilter` key was always the last-editor
// filter (it stored emails) -- the "owner" naming was a misnomer. Keep the
// editor filter pointed at it so users' saved selections survive this
// change, and give the genuinely-new owner filter its own key.
const EDITOR_FILTER_STORAGE_KEY = 'networkBrowserOwnerFilter';
const OWNER_FILTER_STORAGE_KEY = 'networkBrowserOwnerIdFilter';
const TYPE_FILTER_STORAGE_KEY = 'networkBrowserTypeFilter';
const SORT_STORAGE_KEY = 'networkBrowserSort';
const ALL_NETWORKS_LABEL = 'All networks';
const OWNER_OWN_LABEL = 'Created by me';
const EDITOR_OWN_LABEL = 'Edited by me';
const SEARCH_DEBOUNCE_MS = 200;
const parseStoredSort = (raw: string | null | undefined): NetworkSort | undefined => {
  const parsed = safeJsonParse(raw);
  if (typeof parsed !== 'object' || parsed === null) return undefined;
  const {
    column,
    direction
  } = parsed as Partial<NetworkSort>;
  if ((column === 'lastEdited' || column === 'created') && (direction === 'asc' || direction === 'desc')) {
    return {
      column,
      direction
    };
  }
  return undefined;
};

// KBE-964: which bulk actions a selected network takes part in. The master is
// never selectable — nothing in the bar applies to it.
const isBulkSelectable = (network: Option): boolean => !network.isMain;
const isBulkDeletable = (network: Option): boolean => !network.isMain && !network.isReleaseBranch && network.status !== NetworkStatus.RELEASED;
const isBulkRetirable = (network: Option): boolean => !network.isMain && network.retiredAt == null;
const isRetiredNetwork = (network: Option): boolean => network.retiredAt != null;

// Lazy load CompareOptionsDialog to avoid bundling react-select on initial page load
const CompareOptionsDialog = dynamic(() => import('./CompareOptionsDialog/CompareOptionsDialog').then(mod => mod.default), {
  ssr: false,
  loading: () => <div data-testid="compare-dialog-loading" style={{
    padding: '20px'
  }}>
        Loading comparison dialog...
      </div>
});
type NetworkPopoverContainerProps = {
  networkId: string | undefined;
  viewId: string | undefined;
  /**
   * When true, the container renders the selector inline as a page
   * widget instead of as a popover with a trigger button. Used by the
   * Network browser landing page so it reuses the full feature set.
   */
  inline?: boolean;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * When true, suppresses the "auto-open when no network is selected"
   * behavior. Used by pages that intentionally do not require a network
   * selection (e.g. the assistant pages).
   */
  disableAutoOpen?: boolean;
  /**
   * Called when a network is picked on a page that has no network in its
   * URL (e.g. `/assistant`, `/optimization-jobs`). Such pages can't be
   * navigated to a per-network URL, so instead of jumping to the digraph
   * the shell updates its sticky selection in place. Omitted on pages
   * where every selection navigates (the network browser).
   */
  onSelectNetwork?: (networkId: string) => void;
};
const NetworkPopoverContainer = ({
  networkId,
  viewId,
  isOpen,
  onOpenChange,
  disableAutoOpen,
  onSelectNetwork,
  inline = false
}: NetworkPopoverContainerProps) => {
  const asyncPush = useAsyncRoutePush();
  // `usePathname()` is typed `string | null` (it can be null during
  // certain server-render / streaming edges). Normalize once.
  const currentUrl = usePathname() ?? '';
  const {
    mutate: createNetwork,
    isPending: isPendingCreate
  } = useCreateNetwork();
  const {
    mutate: handleDelete,
    isPending: isPendingDelete,
    variables: networkBeingDeleted
  } = useNetworkDelete();
  // getNetworksWithLastEditorPage no longer force-refetches on the realtime
  // broadcast these mutations trigger (KBE-670 -- that refetch was
  // reshuffling pages mid-scroll on OTHER users' edits), so any mutation
  // that changes a list-visible field needs its own explicit refetch to
  // see the result, matching useCreateNetwork/useDeleteNetwork/useDuplicateNetwork.
  const refetchNetworks = useRefetch();
  const {
    mutateAsync: retireNetworkMutation
  } = trpc.network.retireNetwork.useMutation({
    onSettled: async () => await refetchNetworks()
  });
  const {
    mutateAsync: unretireNetworkMutation
  } = trpc.network.unretireNetwork.useMutation({
    onSettled: async () => await refetchNetworks()
  });
  const {
    mutateAsync: renameNetwork,
    isPending: isPendingRename
  } = trpc.network.renameNetwork.useMutation({
    onSettled: async () => await refetchNetworks()
  });
  const {
    mutateAsync: setNetworkVersion,
    isPending: isPendingVersion
  } = trpc.network.setNetworkVersion.useMutation({
    onSettled: async () => await refetchNetworks()
  });
  const {
    mutate: duplicateNetwork,
    isPending: isPendingDuplicate
  } = useDuplicateNetwork();
  const {
    mutateAsync: updateAlgorithm,
    isPending: isPendingAlgorithmUpdate
  } = trpc.network.updateAlgorithm.useMutation({
    onSettled: async () => await refetchNetworks()
  });
  const {
    mutateAsync: releaseNetwork
  } = trpc.network.releaseNetwork.useMutation({
    onSettled: async () => await refetchNetworks()
  });
  const {
    mutateAsync: applyBasePopulation
  } = trpc.variable.applyBasePopulationFromRequirements.useMutation();
  const {
    mutateAsync: setTransitionDefaults
  } = trpc.network.setTransitionDefaults.useMutation();
  const {
    importNetwork
  } = useImportNetwork();
  const {
    run: runBulkAction,
    isPending: isPendingBulkAction,
    pendingDeleteIds: networksBeingBulkDeleted
  } = useBulkNetworkActions();
  const [selectedNetworkIds, setSelectedNetworkIds] = useState<ReadonlySet<string>>(new Set());
  const [bulkDeleteTargets, setBulkDeleteTargets] = useState<BulkNetworkTarget[] | undefined>();
  const [filterValue, setFilterValue] = useState('');
  const [debouncedFilterValue, setDebouncedFilterValue] = useState('');
  const [showRetired, setShowRetired] = useState(false);
  // Debounce the search input before it hits the paged query so each
  // keystroke doesn't trigger a fresh DB round trip.
  useEffect(() => {
    const id = window.setTimeout(() => setDebouncedFilterValue(filterValue), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(id);
  }, [filterValue]);
  const popoverActive = inline || isOpen;
  const {
    network: currentNetwork
  } = useNetwork({
    networkId: networkId ?? ''
  });
  const handleNetworkComparison = useNetworkComparison();
  const handleExportForRelease = useExportReleaseNetwork();
  const handleExportAllNetworkRelatedData = useExportAllNetworkRelatedData();
  const handleGenerateReport = useGenerateNetworkReport();
  const deleteConfirmationDialogRef = useRef(null);
  const bulkDeleteConfirmationDialogRef = useRef(null);
  const exportConfirmationDialogRef = useRef(null);
  const reportConfirmationDialogRef = useRef(null);
  const releaseConfirmationDialogRef = useRef(null);
  const transitionDefaultsDialogRef = useRef(null);
  const handleNetworkChange = useGlobalState(state => state.handleNetworkChange);
  const [deleteConfirmationProps, setDeleteConfirmationProps] = useState<{
    networkName: string;
    onConfirm: () => Promise<void>;
  } | undefined>(undefined);
  const [networkCompareConfirmationProps, setNetworkCompareConfirmationProps] = useState<ConfirmationProps | undefined>(undefined);
  const [isCompareLoading, setIsCompareLoading] = useState(false);
  const [exportConfirmationProps, setExportConfirmationProps] = useState<ExportConfirmationProps | undefined>();
  const [reportConfirmationProps, setReportConfirmationProps] = useState<ReportConfirmationProps | undefined>();
  const [releaseConfirmationProps, setReleaseConfirmationProps] = useState<{
    networkId: string;
    networkName: string;
  } | undefined>();
  const [transitionDefaultsProps, setTransitionDefaultsProps] = useState<{
    networkId: string;
    networkName: string;
  } | undefined>();
  // Fetched per network rather than read off the popover's list row: the menu
  // opens for any row, and the paged list query is hand-written SQL that would
  // have to grow two columns for one dialog.
  const {
    data: transitionDefaultsData
  } = trpc.network.getTransitionDefaults.useQuery({
    networkId: transitionDefaultsProps?.networkId ?? ''
  }, {
    enabled: !!transitionDefaultsProps
  });

  // Inline-only: two independent filters. Owner (network.owner_id) is the
  // primary "who created this" filter; editor (history.author) is the
  // retained "who last touched this" filter. Both are held here so the
  // headerItem dropdowns and the paged query stay in sync. The "mine"
  // branches are resolved server-side (owner from the caller's profile
  // id, editor from ctx.user.email); the client just sends `{kind:'own'}`.
  // `useJsonLocalStorage` returns the default on the server and on the
  // first client render (matching SSR), then the real stored value once
  // mounted -- `enabled: inline` keeps the non-inline popover from ever
  // reading or clobbering the shared keys.
  const [ownerFilter, setOwnerFilter] = useJsonLocalStorage<NetworkPersonFilter>(OWNER_FILTER_STORAGE_KEY, parseStoredPersonFilter, {
    kind: 'all'
  }, inline);
  const [editorFilter, setEditorFilter] = useJsonLocalStorage<NetworkPersonFilter>(EDITOR_FILTER_STORAGE_KEY, parseStoredPersonFilter, {
    kind: 'all'
  }, inline);
  const [typeFilter, setTypeFilter] = useJsonLocalStorage<ReadonlyArray<NetworkTypeFilterOption>>(TYPE_FILTER_STORAGE_KEY, parseStoredTypeFilter, [], inline);
  // Sort column + direction for the inline browser. Persisted so a chosen
  // sort survives reloads, matching the filters and column widths.
  const [sort, setSort] = useJsonLocalStorage<NetworkSort>(SORT_STORAGE_KEY, parseStoredSort, DEFAULT_NETWORK_SORT, inline);
  // Clicking a sortable header toggles direction when it's already the
  // active column, otherwise switches to that column starting descending
  // (newest-first, the more useful default for both timestamps).
  const handleSort = useCallback((column: NetworkSortColumn) => {
    setSort(sort.column === column ? {
      column,
      direction: sort.direction === 'desc' ? 'asc' : 'desc'
    } : {
      column,
      direction: 'desc'
    });
  }, [setSort, sort]);
  // Paged network list: server-side cursor pagination, search filter,
  // and owner filter. Each filter change is a new query key; previous
  // pages stay visible (keepPreviousData) while the new first page
  // loads.
  const {
    networks,
    isLoading: isLoadingNetworks,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage
  } = useNetworks({
    enabled: popoverActive,
    search: debouncedFilterValue,
    ownerFilter,
    editorFilter,
    typeFilter,
    sort,
    includeRetired: showRetired
  });

  // Owner-filter dropdown candidates: distinct profiles that own at least
  // one network. Editor-filter candidates: distinct latest-history authors.
  // Both are cheap aggregates, safe to fetch eagerly when the selector opens.
  const {
    data: ownersData
  } = trpc.network.listDistinctNetworkOwners.useQuery(undefined, {
    enabled: popoverActive
  });
  const ownerOptions = useMemo(() => ownersData?.map(o => ({
    id: o.id,
    label: o.name ?? 'Unknown'
  })) ?? [], [ownersData]);
  const {
    data: editorsData
  } = trpc.network.listDistinctNetworkEditors.useQuery(undefined, {
    enabled: popoverActive
  });
  const editorOptions = useMemo(() => editorsData?.map(e => ({
    id: e.email,
    label: e.email
  })) ?? [], [editorsData]);

  // Lightweight {id, name, status} list for call sites that need every
  // network — compare dialog, duplicate-name uniqueness, release/export
  // name lookup. Fetched separately so the paged main query can stay
  // page-sized.
  const {
    data: allNetworkNamesData,
    isLoading: isLoadingAllNetworkNames
  } = trpc.network.listNetworkNamesForLists.useQuery(undefined, {
    enabled: popoverActive
  });
  const allNetworkNames = useMemo(() => allNetworkNamesData ?? [], [allNetworkNamesData]);
  const invalidateNetwork = useInvalidateNetwork();
  const trpcUtils = trpc.useUtils();
  const isLoading = isPendingCreate || isLoadingNetworks || isLoadingAllNetworkNames || isPendingRename || isPendingVersion || isPendingDuplicate || isPendingAlgorithmUpdate;
  const handleDeleteAttempt = useCallback(({
    id: id_0,
    name
  }: {
    id: string;
    name: string;
  }) => {
    setDeleteConfirmationProps({
      networkName: name,
      onConfirm: async () => {
        await handleDelete(id_0);
        if (networkId === id_0) {
          await asyncPush('/');
        }
      }
    });
  }, [asyncPush, handleDelete, networkId]);
  const handleRetireAttempt = useCallback(async ({
    id: id_1,
    name: name_0
  }: {
    id: string;
    name: string;
  }) => {
    await mutationWithFeedback({
      mutation: retireNetworkMutation({
        id: id_1
      }),
      successText: `Retired "${name_0}"`,
      errorText: 'Failed to retire network'
    });
  }, [retireNetworkMutation]);
  const handleUnretireAttempt = useCallback(async ({
    id: id_2,
    name: name_1
  }: {
    id: string;
    name: string;
  }) => {
    await mutationWithFeedback({
      mutation: unretireNetworkMutation({
        id: id_2
      }),
      successText: `Unretired "${name_1}"`,
      errorText: 'Failed to unretire network'
    });
  }, [unretireNetworkMutation]);
  const handleCompareAttempt = useCallback(({
    id: id_3,
    name: name_2
  }: {
    id: string;
    name: string;
  }) => {
    setNetworkCompareConfirmationProps({
      networkName: name_2,
      networkId: id_3,
      isLoading: false,
      onConfirm: async (networkIds: {
        oldNetworkId: string;
        newNetworkId: string;
      }, options) => {
        setIsCompareLoading(true);
        try {
          await handleNetworkComparison(networkIds, options, allNetworkNames);
        } catch (error) {
          console.error('Network comparison failed:', error);
          showError('Failed to compare networks');
        } finally {
          setIsCompareLoading(false);
        }
      }
    });
  }, [handleNetworkComparison, allNetworkNames]);
  const handleExportAttempt = useCallback(({
    id: id_4,
    name: name_3
  }: {
    id: string;
    name: string;
  }) => {
    setExportConfirmationProps({
      networkName: name_3,
      onConfirm: async options_0 => {
        if (options_0.type === ExportType.Release) {
          await handleExportForRelease(id_4, allNetworkNames).catch(() => {
            showError('Failed to export network');
          });
        }
        if (options_0.type === ExportType.Import) {
          await handleExportAllNetworkRelatedData(id_4, options_0.historyLimit).catch(e_0 => {
            console.error(e_0);
            showError('Failed to export network');
          });
        }
      }
    });
  }, [handleExportAllNetworkRelatedData, handleExportForRelease, allNetworkNames]);
  const handleReportAttempt = useCallback(({
    id: id_5,
    name: name_4
  }: {
    id: string;
    name: string;
    inferenceAlgorithm: InferenceAlgorithm;
  }) => {
    setReportConfirmationProps({
      networkName: name_4,
      onConfirm: async options_1 => {
        await handleGenerateReport(id_5, allNetworkNames, options_1).catch(() => {
          showError('Failed to export network');
        });
      }
    });
  }, [handleGenerateReport, allNetworkNames]);

  // Releasing now requires an explicit version (KBE-544), so the action
  // opens a dialog to collect name + version before the mutation fires.
  const handleReleaseAttempt = useCallback(({
    id: id_6,
    name: name_5
  }: {
    id: string;
    name: string;
  }) => {
    setReleaseConfirmationProps({
      networkId: id_6,
      networkName: name_5
    });
  }, []);
  const handleReleaseConfirm = useCallback(async (networkId_0: string, newName: string, version: string) => {
    await mutationWithFeedback({
      mutation: releaseNetwork({
        networkId: networkId_0,
        newName,
        version
      }).then(result => {
        if ('errors' in result) {
          // Release checks failed. We'll show the errors in a file.
          const networkName = allNetworkNames.find(n => n.id === networkId_0)?.name;
          const fileName = `${networkName?.replaceAll(' ', '_') ?? 'unknown_network'}_release_errors.json`;
          downloadJsonFile({
            errors: result.errors
          }, fileName);
          throw new ReleaseValidationError(result.errors);
        }
      }),
      successText: 'Network released',
      errorText: 'Failed to release network',
      onError: error_0 => handleNetworkNameUniqueViolationError(error_0)
    });
  }, [releaseNetwork, allNetworkNames]);
  const handleTransitionDefaultsAttempt = useCallback(({
    id: id_7,
    name: name_6
  }: {
    id: string;
    name: string;
  }) => {
    setTransitionDefaultsProps({
      networkId: id_7,
      networkName: name_6
    });
  }, []);
  const handleApplyBasePopulation = useCallback(async ({
    id: id_8
  }: {
    id: string;
    name: string;
  }) => {
    const loadingToast = toaster.create({
      type: 'loading',
      title: 'Applying base population...'
    });
    try {
      const result_0 = await applyBasePopulation({
        networkId: id_8,
        dryRun: false
      });
      toaster.dismiss(loadingToast);
      if (result_0.updates.length === 0) {
        toaster.create({
          type: 'info',
          title: 'No variables to update',
          description: 'No variables found with 0 EXP requirements in single-sex assumption sets',
          duration: 5000
        });
        return;
      }
      const variableNames = result_0.updates.map(u => u.variableName).slice(0, 3).join(', ');
      const moreVariables = result_0.updates.length > 3 ? ` +${result_0.updates.length - 3} more` : '';
      toaster.create({
        type: 'success',
        title: `Updated ${result_0.updatedCount} variable(s)`,
        description: `Variables: ${variableNames}${moreVariables}. Requirements migrated: ${result_0.totalDeletedRequirements} deleted, ${result_0.totalCreatedRequirements} created.`,
        duration: 8000,
        meta: {
          closable: true
        }
      });
    } catch (error_1) {
      toaster.dismiss(loadingToast);
      toaster.create({
        type: 'error',
        title: 'Failed to apply base population',
        description: error_1 instanceof Error ? error_1.message : 'Unknown error',
        duration: 5000
      });
    }
  }, [applyBasePopulation]);
  const menuItems: MenuItems = useMemo(() => {
    const networkNames = allNetworkNames.map(n_0 => n_0.name);
    return id_9 => {
      const network = networks.find(n_1 => n_1.id === id_9);
      // Read status from `networks` (which includes retired rows when "Show
      // retired" is on), not `allNetworkNames` — the latter excludes retired
      // networks, so a retired released network would look un-released there
      // and wrongly enable the Release action.
      const isReleased = network?.status === NetworkStatus.RELEASED;
      const isRetired = network?.retiredAt != null;
      // The master can never be retired (guarded server-side); omit the item
      // entirely rather than show an action that always errors.
      const isMain = network?.isMain ?? false;
      const isProtectedRef = isMain || network?.isReleaseBranch === true;
      return [generateRenameMenuItem(({
        id: id_10,
        name: name_7
      }) => mutationWithFeedback({
        mutation: renameNetwork({
          id: id_10,
          newName: name_7
        }),
        successText: 'Renamed network',
        errorText: 'Failed to rename network'
      })), generateVersionMenuItem(({
        id: id_11,
        version: version_0
      }) => mutationWithFeedback({
        mutation: setNetworkVersion({
          id: id_11,
          version: version_0.trim() === '' ? null : version_0
        }),
        successText: 'Updated version',
        errorText: 'Failed to update version'
      })), generateDuplicateMenuItem(newNetwork => duplicateNetwork({
        ...newNetwork,
        // `viewId` is the view open in the *current* network, but any row
        // in the list can be duplicated. Carrying it over to a copy of a
        // different network would look for a view that never existed
        // there. See KBE-777.
        redirectToView: newNetwork.id === networkId ? viewId : undefined
      }), networkNames,
      // Resolved per row, not from the open network, since any row in the
      // list can be copied.
      {
        allowRefBranch: isProtectedRef
      }), generateDeleteMenuItem(handleDeleteAttempt), generateExportMenuItem(handleExportAttempt), generateReportMenuItem(handleReportAttempt), generateCompareMenuItem(handleCompareAttempt), ...(isMain ? [] : [isRetired ? generateUnretireMenuItem(args => void handleUnretireAttempt(args)) : generateRetireMenuItem(args_0 => void handleRetireAttempt(args_0))]), generateAlgorithmMenuItem(({
        id: id_12,
        algorithm
      }) => mutationWithFeedback({
        mutation: updateAlgorithm({
          id: id_12,
          algorithm
        }).then(async () => {
          await invalidateNetwork(id_12);
        }),
        successText: 'Updated algorithm',
        errorText: 'Failed to update algorithm'
      })), generateReleaseMenuItem({
        action: handleReleaseAttempt,
        disabled: isReleased
      }), generateApplyBasePopulationMenuItem(handleApplyBasePopulation), generateTransitionDefaultsMenuItem(handleTransitionDefaultsAttempt), generateViewCommitsMenuItem(({
        id: id_13
      }) => {
        void asyncPush(`/${id_13}/commits`);
      })];
    };
  }, [asyncPush, duplicateNetwork, handleApplyBasePopulation, handleCompareAttempt, handleDeleteAttempt, handleExportAttempt, handleReleaseAttempt, handleReportAttempt, handleRetireAttempt, handleTransitionDefaultsAttempt, handleUnretireAttempt, allNetworkNames, networkId, networks, renameNetwork, setNetworkVersion, updateAlgorithm, viewId, invalidateNetwork]);

  // The server already filters by name (debounced), owner, and excludes
  // snapshots. The only client-side filter left is the optimistic hide
  // of a row that's mid-deletion.
  const networkOptions = useMemo(() => networks.filter(n_2 => !(isPendingDelete && networkBeingDeleted && n_2.id === networkBeingDeleted.id) && !networksBeingBulkDeleted.has(n_2.id)).map(network_0 => ({
    ...network_0,
    // Keep the user on the page they're on (history, diagnosis,
    // assumption-set, …) and just swap the network, instead of
    // always jumping to the digraph. The view id is dropped — it
    // belonged to the previous network. On pages with no network in
    // the URL the helper returns null; we fall back to the current
    // path so the row click is a no-op navigation and the sticky
    // selection update (see onChangeCurrentOption) drives the
    // refresh instead.
    url: buildNetworkSwitchUrl(currentUrl, network_0.id) ?? currentUrl
  })), [currentUrl, isPendingDelete, networkBeingDeleted, networksBeingBulkDeleted, networks]);

  // Only rows currently in the list take part in a bulk action, so narrowing a
  // filter can never act on a network the researcher can no longer see.
  const selectedNetworks = useMemo(() => networkOptions.filter(n_3 => selectedNetworkIds.has(n_3.id)), [networkOptions, selectedNetworkIds]);
  const selection: OptionSelection | undefined = useMemo(() => inline ? {
    selectedIds: selectedNetworkIds,
    isSelectable: isBulkSelectable,
    onToggle: id_14 => setSelectedNetworkIds(previous => {
      const next = new Set(previous);
      if (!next.delete(id_14)) next.add(id_14);
      return next;
    }),
    onSetSelected: (ids, selected) => setSelectedNetworkIds(previous_0 => {
      const next_0 = new Set(previous_0);
      for (const id_15 of ids) {
        if (selected) next_0.add(id_15);else next_0.delete(id_15);
      }
      return next_0;
    })
  } : undefined, [inline, selectedNetworkIds]);
  const handleBulkAction = useCallback(async (action: BulkNetworkAction, targets: BulkNetworkTarget[]): Promise<void> => {
    if (targets.length === 0) return;
    setSelectedNetworkIds(new Set());
    const succeededIds = await runBulkAction(action, targets);
    if (action === 'delete' && networkId && succeededIds.includes(networkId)) {
      await asyncPush('/');
    }
  }, [asyncPush, networkId, runBulkAction]);
  const toBulkTargets = (predicate: (network: Option) => boolean): BulkNetworkTarget[] => selectedNetworks.filter(predicate).map(({
    id: id_16,
    name: name_8
  }) => ({
    id: id_16,
    name: name_8
  }));

  // Name uniqueness uses the full lite list so the duplicate check
  // doesn't depend on which pages have been loaded. While the lite
  // list is still loading we conservatively treat any non-empty name
  // as not-creatable to avoid letting the user submit a name that
  // turns out to already exist.
  const isNetworkCreatable = useMemo(() => {
    if (isLoadingAllNetworkNames) return false;
    return allNetworkNames.findIndex(n_4 => n_4.name === filterValue.trim()) === -1;
  }, [filterValue, allNetworkNames, isLoadingAllNetworkNames]);

  // Inline-only empty state: distinguish "DB has no networks at all"
  // from "the current filter combo hides every network" so the user
  // can tell whether the fix is "create one" or "drop a filter".
  const hasNameFilter = filterValue.trim().length > 0;
  const hasOwnerFilter = ownerFilter.kind !== 'all';
  const hasEditorFilter = editorFilter.kind !== 'all';
  const hasTypeFilter = typeFilter.length > 0;
  const hasAnyFilter = hasNameFilter || hasOwnerFilter || hasEditorFilter || hasTypeFilter;
  const handleClearFilters = (): void => {
    setFilterValue('');
    setOwnerFilter({
      kind: 'all'
    });
    setEditorFilter({
      kind: 'all'
    });
    setTypeFilter([]);
  };
  const activeFilterSummary = [hasNameFilter ? `Name "${filterValue.trim()}"` : null, hasOwnerFilter ? describePersonFilter(ownerFilter, ownerOptions, ALL_NETWORKS_LABEL, OWNER_OWN_LABEL) : null, hasEditorFilter ? describePersonFilter(editorFilter, editorOptions, ALL_NETWORKS_LABEL, EDITOR_OWN_LABEL) : null, hasTypeFilter ? describeTypeFilter(typeFilter) : null].filter((s): s is string => s !== null).join(' · ');
  // Avoid claiming "no networks yet" while the lite list is still
  // loading — that branch should only fire once we know the DB is
  // genuinely empty.
  const inlineEmptyState = !inline ? undefined : isLoadingAllNetworkNames ? undefined : allNetworkNames.length === 0 ? <Text color="gray.500">No networks yet — create one above.</Text> : networkOptions.length === 0 ? <Stack align="center" gap={3}>
        <Text color="gray.700" fontWeight="medium">
          No networks match the current filter.
        </Text>
        <Text color="gray.500" fontSize="sm">
          {activeFilterSummary}
        </Text>
        {hasAnyFilter && <Button aria-label="clear-network-browser-filters" size="sm" variant="outline" onClick={handleClearFilters}>
            Clear filters
          </Button>}
      </Stack> : undefined;
  return <>
      <VirtualizedSelectPopover currentOption={networkId} currentOptionName={currentNetwork?.name} hasMore={hasNextPage} headerItem={<Flex direction="column" gap={2} width="100%">
            <Flex gap={4} justifyContent="space-between" paddingRight={inline ? 0 : 10} width="100%">
              <CreateInput isCreateDisabled={!isNetworkCreatable} placeholder="Filter or create..." onChange={setFilterValue} onCreate={createNetwork} />
              <ImportNetworkButton importNetwork={importNetwork} />
            </Flex>
            {inline && <Flex gap={1} wrap="wrap">
                <NetworkPersonFilterDropdown allLabel={ALL_NETWORKS_LABEL} ariaLabel="Filter networks by owner" options={ownerOptions} ownLabel={OWNER_OWN_LABEL} sectionLabel="Filter by owner" value={ownerFilter} onChange={setOwnerFilter} />
                <NetworkPersonFilterDropdown allLabel={ALL_NETWORKS_LABEL} ariaLabel="Filter networks by last editor" options={editorOptions} ownLabel={EDITOR_OWN_LABEL} sectionLabel="Filter by last editor" value={editorFilter} onChange={setEditorFilter} />
                <NetworkTypeFilterDropdown value={typeFilter} onChange={setTypeFilter} />
                <Switch aria-label="toggle-show-retired-networks" checked={showRetired} size="sm" onCheckedChange={d => setShowRetired(d.checked)}>
                  Show retired
                </Switch>
              </Flex>}
            {inline && selectedNetworks.length > 0 && <NetworkBulkActionBar deletableCount={selectedNetworks.filter(isBulkDeletable).length} isPending={isPendingBulkAction} maxBatchSize={MAX_BULK_NETWORK_ACTION_SIZE} retirableCount={selectedNetworks.filter(isBulkRetirable).length} retiredCount={selectedNetworks.filter(isRetiredNetwork).length} selectedCount={selectedNetworks.length} onClear={() => setSelectedNetworkIds(new Set())} onDelete={() => setBulkDeleteTargets(toBulkTargets(isBulkDeletable))} onRetire={() => void handleBulkAction('retire', toBulkTargets(isBulkRetirable))} onUnretire={() => void handleBulkAction('unretire', toBulkTargets(isRetiredNetwork))} />}
          </Flex>} icon={<IoIosGitNetwork />} inline={inline} inlineEmptyState={inlineEmptyState} isDefaultOpen={!networkId && !disableAutoOpen} isFetchingMore={isFetchingNextPage} isLoading={isLoading} isOpen={isOpen} menuItems={menuItems} options={networkOptions} refOfElementsToIgnoreOutsideClick={[deleteConfirmationDialogRef, bulkDeleteConfirmationDialogRef, exportConfirmationDialogRef, reportConfirmationDialogRef, releaseConfirmationDialogRef, transitionDefaultsDialogRef]} selection={selection} shouldPreventClose={() => !!deleteConfirmationProps || !!bulkDeleteTargets || !!networkCompareConfirmationProps || !!exportConfirmationProps || !!reportConfirmationProps || !!releaseConfirmationProps || !!transitionDefaultsProps} sortColumn={sort.column} sortDirection={sort.direction} title={Title.NETWORK} onChangeCurrentOption={async (id_17: string) => {
      // On a page with no network in its URL the row click is a no-op
      // navigation (url === currentUrl); update the shell's sticky
      // selection instead so the breadcrumb (and Marvin) reflect the
      // new network without leaving the page.
      if (buildNetworkSwitchUrl(currentUrl, id_17) === null) {
        onSelectNetwork?.(id_17);
        return;
      }
      handleNetworkChange();
      if (networkId === id_17) {
        await invalidateNetwork(networkId);
      }
    }} onCreate={createNetwork} onLoadMore={fetchNextPage} onOpenChange={onOpenChange} onSort={handleSort} />
      {deleteConfirmationProps && <ConfirmationDialog ref={deleteConfirmationDialogRef} isOpen confirmingActionText="Delete" description="Deleting a network is irreversible and all variables will be lost." title={`Are you sure you want to delete ${deleteConfirmationProps.networkName}?`} onClose={() => setDeleteConfirmationProps(undefined)} onConfirm={async () => await deleteConfirmationProps.onConfirm()} />}
      {bulkDeleteTargets && <ConfirmationDialog ref={bulkDeleteConfirmationDialogRef} isOpen confirmingActionText={`Delete ${bulkDeleteTargets.length}`} description="Deleting a network is irreversible and all its variables will be lost." title={`Delete ${bulkDeleteTargets.length} network${bulkDeleteTargets.length === 1 ? '' : 's'}?`} onClose={() => setBulkDeleteTargets(undefined)} onConfirm={() => void handleBulkAction('delete', bulkDeleteTargets)}>
          <Box maxHeight="15rem" overflowY="auto">
            <List.Root aria-label="bulk-delete-network-names" ps={5}>
              {bulkDeleteTargets.map(target => <List.Item key={target.id}>{target.name}</List.Item>)}
            </List.Root>
          </Box>
        </ConfirmationDialog>}
      {networkCompareConfirmationProps && <CompareOptionsDialog confirmationProps={{
      ...networkCompareConfirmationProps,
      isLoading: isCompareLoading
    }} networks={allNetworkNames.map(n_5 => ({
      id: n_5.id,
      name: n_5.name
    }))} onClose={() => setNetworkCompareConfirmationProps(undefined)} />}
      <ExportOptionsDialog ref={exportConfirmationDialogRef} confirmationProps={exportConfirmationProps} onClose={() => setExportConfirmationProps(undefined)} />
      <ReportOptionsDialog ref={reportConfirmationDialogRef} confirmationProps={reportConfirmationProps} onClose={() => setReportConfirmationProps(undefined)} />
      {transitionDefaultsProps && <TransitionDefaultsDialog ref={transitionDefaultsDialogRef} isOpen halfBandPercent={transitionDefaultsData?.defaultTransitionHalfBandPercent} networkName={transitionDefaultsProps.networkName} onClose={() => setTransitionDefaultsProps(undefined)} onSubmit={async halfBandPercent => {
      await mutationWithFeedback({
        mutation: setTransitionDefaults({
          networkId: transitionDefaultsProps.networkId,
          defaultTransitionHalfBandPercent: halfBandPercent
        }).then(async () => {
          // Every inheriting variable's curve just moved, so the loaded
          // network is stale — the digraph and diagnosis both resolve the
          // default from it.
          //
          // `getTransitionDefaults` has to be invalidated too, and it is
          // not covered by `invalidateNetwork` (which only touches
          // `getNetwork`). The global `staleTime` is Infinity, so without
          // this the dialog re-reads its first fetch forever and reopening
          // it shows the pre-edit values as though the save never landed.
          await Promise.all([invalidateNetwork(transitionDefaultsProps.networkId), trpcUtils.network.getTransitionDefaults.invalidate({
            networkId: transitionDefaultsProps.networkId
          })]);
        }),
        successText: 'Updated transition defaults',
        errorText: 'Failed to update transition defaults'
      });
    }} />}
      {releaseConfirmationProps && <ReleaseDialog ref={releaseConfirmationDialogRef} isOpen networkName={releaseConfirmationProps.networkName} onClose={() => setReleaseConfirmationProps(undefined)} onSubmit={version_1 =>
    // No separate release-name field (for now) — the released
    // network is named after its version.
    handleReleaseConfirm(releaseConfirmationProps.networkId, version_1, version_1)} />}
    </>;
};
export default NetworkPopoverContainer;
function handleNetworkNameUniqueViolationError(error: unknown) {
  if (error instanceof TRPCClientError && error.message === NETWORK_NAME_UNIQUE_VIOLATION_ERROR) {
    return {
      overrideToast: {
        title: NETWORK_NAME_UNIQUE_VIOLATION_ERROR,
        description: 'Choose a different name',
        duration: 5000
      }
    };
  }
  return undefined;
}